import React, { Fragment, useEffect, useRef, useState } from "react";
import { useEffectOnce } from "../../../hooks/useeffectonce";
import { useSelector, useDispatch } from "react-redux";

import { addNotification } from "../../../redux/slices/notificationsslice";
import {
  setCurrentPageIsDirty,
  setCurrentPage,
} from "../../../redux/slices/currentpageslice";
import { notificationDurationMiddle } from "../../../lib/utils";
import { useNavigate, useLocation } from "react-router-dom";
import { apiErrorsHandler, getErrorMessages } from "../../../api/httpclient";
import { Page } from "../../page";
import { deepCompare } from "../../../lib/utils";
import { BLOCK_DIRTY_MESSAGE } from "../../../settings";

import "./createdatasetpage.css";
import {
  setProcessing,
  disposePage,
  loadPage,
  setPanes,
  setPageValue,
  setFormValue,
} from "../../../redux/slices/createdatasetpageslice";
import { CreateDatasetPageBody } from "./createdatasetpagebody";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const CreateDatasetPage = (props) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.createDatasetPage);
  const navigate = useNavigate();
  const currentPageState = useSelector((state) => state.currentPage);

  let currentPageId = "createDatasetPage";
  const { t, i18n } = useTranslation();

  /*block navigation if dirty*/
  const updateDirtyState = () => {
    let isDirty = !deepCompare(
      pageState.formInitialValues,
      pageState.formValues
    );
    let wasDirty = currentPageState.isDirty;

    if (isDirty != wasDirty) dispatch(setCurrentPageIsDirty(isDirty));
  };

  const blockNavigationIfDirty = (msg) => {
    if (currentPageState.isDirty) {
      if (window.confirm(msg ? msg : BLOCK_DIRTY_MESSAGE) == true) {
        dispatch(setCurrentPageIsDirty(false));
        return false;
      } else return true;
    } else return false;
  };
  /*block navigation if dirty*/

  /*splitter*/
  const handleOnSplitterChange = (e) => {
    dispatch(setPanes(e.newState));
  };
  /*splitter*/

  /*initial page load*/
  useEffectOnce(() => {
    dispatch(loadPage(currentUser, t)).then(
      (response) => {
        dispatch(setCurrentPage({ id: currentPageId, isDirty: false }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("datasets.create.loading_error"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datasets.create.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
          navigate("/", { replace: true });
        };

        //console.log(action);
        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );

    return () => {
      dispatch(setCurrentPage({ id: "", isDirty: false }));
      dispatch(disposePage(true));
    };
  }, []);
  /*initial load*/

  /*toolbar*/
  const handleOnNew = (e) => { 
    if (blockNavigationIfDirty() == true) return;
    dispatch(loadPage(currentUser, t)).then(
      (response) => {
        dispatch(setCurrentPage({ id: currentPageId, isDirty: false }));
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          //notify with error
          if (!errorMessages || errorMessages.length == 0)
            dispatch(
              addNotification({
                text: t("datasets.create.loading_error"),
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          else {
            errorMessages = getErrorMessages(errorMessages, 1);
            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              })
            );
          }

          //go home
          navigate("/", { replace: true });
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("datasets.create.unknown_error"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
          navigate("/", { replace: true });
        };

        //console.log(action);
        apiErrorsHandler({}, dispatch, navigate, err, "/", {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };

  const handleOnClose = (e) => {
    if (blockNavigationIfDirty() == true) return;
    navigate("/datasets");
  };

  //tools visible/hidden
  const handleOnToolsToggle = (e) => {
    const isVisible = pageState.toolsVisible;

    if (isVisible)
      //hide
      dispatch(
        setPanes([
          {
            size: "0%",
            min: "%",
            max: "%",
            collapsible: false,
          },
          {
            min: "100%",
            collapsible: false,
          },
        ])
      );
    else
      dispatch(
        setPanes([
          {
            size: "20%",
            min: "20%",
            max: "25%",
            collapsible: false,
          },
          {
            min: "70%",
            max: "80%",
            collapsible: false,
          },
        ])
      );

    dispatch(setPageValue({ id: "toolsVisible", value: !isVisible }));
    //dispatch(redrawTiles());
  };

  //     //make sure no client errors; if so return
  //     let formErrors = formValidator(pageState.formValues);
  //     if (formErrors !== undefined)
  //     {
  //         dispatch(addNotification({text: "The entity contains invalid information; hence, it cannot be persisted.", type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
  //         return;
  //     }

  //     //no client errors => proceed
  //     dispatch(saveEntity()).then(
  //         response =>{
  //             dispatch(setCurrentPageIsDirty(false));
  //         },
  //         err =>
  //         {
  //             //hide loader
  //             dispatch(setProcessing({processing: false}));

  //             const handleResponseReceived = (state, dispatch, navigate, payload) => {
  //                 let errorMessages = payload?.data?.ErrorMessages;

  //                 if (!errorMessages || errorMessages.length == 0)
  //                     errorMessages = ["An unknown error occured!"];
  //                 else
  //                 {
  //                     errorMessages = getErrorMessages(errorMessages, 1);

  //                     //if the error is entity does not exist => it has been removed => remove it from currentDataset and from editing
  //                     if (payload.data.ErrorCodes.EntityDoesNotExist)
  //                     {
  //                         dispatch(removeEntityFromUI(pageState.formValues.Id));
  //                         dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
  //                     }
  //                     else
  //                         dispatch(setFormServerErrors(errorMessages));
  //                 }
  //             };

  //             const handleUnhandled = (state, dispatch, navigate, payload) =>{
  //                 let errorMessage = "An unknown error occured!";
  //                 dispatch(setFormServerErrors([errorMessage]));
  //             };

  //             apiErrorsHandler({}, dispatch, navigate, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});
  //         }
  //     );
  // };

  // const handleOnClone = e => {
  //     dispatch(initEntityCloner({title: pageState.formValues.Title, navigate: true}));

  //     dispatch(setAppDialog({
  //         isVisible: true, closable: true, title: "Clone Data Source", className: "",
  //         content: <EntityCloner className=""/>,
  //         actions: {
  //           yesButton: {
  //             title: "Clone", handler: e => {
  //                 dispatch(cloneDataSource()).then(
  //                     response => {
  //                         dispatch(hideAppDialog());

  //                         dispatch(addNotification({text: "Data source cloned successfully", type: "success", closable: true, duration:notificationDurationMiddle, position:"topRight"}));

  //                         dispatch(entityCloned({id: response.id, title: response.title, navigate: response.navigate}));
  //                     },
  //                     err => {
  //                         if (err == "invalidData")
  //                         {
  //                             //show notification that title is required
  //                             dispatch(addNotification({text: "Please, specify the cloned data source title", type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
  //                             return;
  //                         }
  //                         else if (err == "ignore")
  //                         {
  //                             //in this case the user clicked clone while it's cloning
  //                             //just do nothing
  //                             return;
  //                         }
  //                         else
  //                         {
  //                             const handleResponseReceived = (state, dispatch, navigate, payload) => {
  //                                 let errorMessages = payload?.data?.ErrorMessages;
  //                                 let errorMessage = "An unknown error occured!";

  //                                 if (errorMessages || errorMessages.length > 0)
  //                                 {
  //                                     errorMessages = getErrorMessages(errorMessages, 1);

  //                                     //if the error is entity does not exist => it has been removed => remove it from currentDataset and from editing
  //                                     if (payload.data.ErrorCodes.TitleExists)
  //                                         errorMessage = errorMessages[0];
  //                                 }

  //                                 dispatch(addNotification({text: errorMessage, type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
  //                             };

  //                             const handleUnhandled = (state, dispatch, navigate, payload) =>{

  //                                 dispatch(addNotification({text: "An unknown error occured!", type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
  //                             };

  //                             apiErrorsHandler({}, dispatch, null, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});

  //                             return Promise.resolve();
  //                         }
  //                     }
  //                 )
  //             }
  //           },
  //           noButton: {
  //             title: "Cancel"
  //           }
  //         }
  //       }));
  // };

  // const handleOnErrorbarClose = e => {
  //     dispatch(setFormServerErrors([]));
  // };
  /*toolbar*/

  /*delete entity*/
  // const handleOnDelete = (id) => {
  //     //id is zero if deleted from toolbar and > 0 if deleted from entities list
  //     //if id is zero and mode !=2 => miust not happen
  //     if (!id && pageState.mode != 2)
  //         return;

  //     if (!id)//deleted from the toolbar => it's the current entity and mode is 2
  //       id = pageState.formValues.Id;

  //       let isCurrentEntity = false;

  //       if (!id)//deleted from the toolbar => it's the current entity and mode is 2
  //       {
  //           id = pageState.formValues.Id;
  //           isCurrentEntity = true;
  //       }
  //       else if (pageState.formValues?.Id && pageState.formValues.Id == id)
  //           isCurrentEntity = true;

  //       if (isCurrentEntity)
  //       {
  //           //if contains dirty => block
  //           if (blockNavigationIfDirty() == true)
  //               return;
  //       }

  //     dispatch(setAppDialog({
  //         isVisible: true,
  //         className: "datasources-page-are-you-sure",
  //         title: "Warning!", content: "Are you sure that you want to delete the specified data source?",
  //         actions:{
  //             yesButton:{ title: "Yes", handler: () =>{
  //                 dispatch(hideAppDialog());

  //                 dispatch(deleteEntity(id))
  //                 .then(
  //                     response =>{
  //                     },
  //                     err => {
  //                         dispatch(setProcessing({processing: false}));

  //                         const handleResponseReceived = (state, dispatch, navigate, payload) => {
  //                             let errorMessages = payload?.data?.ErrorMessages;

  //                             if (!errorMessages || errorMessages.length == 0)
  //                                 errorMessages = ["An unknown error occured!"];
  //                             else
  //                             {
  //                                 errorMessages = getErrorMessages(errorMessages, 1);

  //                                 if (payload.data.ErrorCodes.EntityDoesNotExist)
  //                                     dispatch(removeEntityFromUI(id));
  //                             }

  //                             dispatch(addNotification({text: errorMessages[0], type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
  //                         };

  //                         const handleUnhandled = (state, dispatch, navigate, payload) =>{
  //                             let errorMessage = "An unknown error occured!";
  //                             dispatch(addNotification({text: errorMessage, type: "error", closable: true, duration:notificationDurationMiddle, position:"topRight"}));
  //                         };

  //                         apiErrorsHandler({}, dispatch, navigate, err, null, {responseReceived: handleResponseReceived, unhandled: handleUnhandled});
  //                     });
  //             }},
  //             noButton: {title: "No"}
  //         }
  //     }));
  // };
  /*delete entity*/

  //#region form
  useEffect(() => {
    const fromFileSteps = [
      { label: t("datasets.create.from"), isValid: true },
      { label: t("datasets.create.choosefile"), isValid: undefined },
      { label: t("datasets.create.dataset"), isValid: undefined },
      { label: t("datasets.create.review"), isValid: undefined },
      { label: t("datasets.create.result"), isValid: undefined },
    ];
    dispatch(setPageValue({ id: "steps", value: fromFileSteps }));
  }, [i18n.language]);

  //#endregion

  return (
    <Fragment>
      <Page
        id="datasetsPage"
        className="datasets-page"
        title="Dataset"
        animation={props.animation}
      >
        <CreateDatasetPageBody
          // onSave={handleOnSave} onClone={handleOnClone} onDelete={handleOnDelete}
          // onErrorbarClose={handleOnErrorbarClose}
          // onEntitySelected={handleOnEntitySelected} onEntityDoubleClick={handleOnEntitySelected}
          onSplitterChange={handleOnSplitterChange}
          onToolsToggle={handleOnToolsToggle}
          onNew={handleOnNew}
          onClose={handleOnClose}
          updateDirtyState={updateDirtyState}
        />
      </Page>
      <style>
        {`
                    /*page class*/
                    .datasets-page
                    {
                        background-color: transparent;
                        padding: ${currentTheme.pagePadding};
                    }

                    .datasets-page .k-splitbar.k-splitbar-horizontal
                    {
                        background-color: ${currentTheme.paneSeparatorBg};
                    }

                    .datasets-page .k-splitbar
                    {
                        width: ${currentTheme.paneSeparatorWidth};
                    }

                    /*are you sure to delete*/
                    .datasets-page-are-you-sure .k-widget
                    {
                      /*width of the dialog*/
                      width: 300px;
                    }    
                    
                    .datasets-page-are-you-sure .k-widget .k-dialog-titlebar.k-header.k-header
                    {
                        /*bg color of the header*/
                        background-color: ${currentTheme.primary};
                    }
                    `}
      </style>
    </Fragment>
  );
};
